import { Node, mergeAttributes } from '@tiptap/core';
import { TemplateSectionType } from '../types/template.types';

export interface DivOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    div: {
      /**
       * Set a div node
       */
      setDiv: (attributes?: DivAttributes) => ReturnType;
      /**
       * Toggle a div wrap
       */
      toggleDiv: (attributes?: DivAttributes) => ReturnType;
    };
  }
}

export interface DivAttributes {
  class?: string | null;
  style?: string | null;
  editable?: boolean;
  sectionId?: string | null;
  sectionType?: TemplateSectionType | null;
  prefillable?: boolean;
}

/**
 * Enhanced Tiptap extension for div elements with support for:
 * - CSS classes and inline styles
 * - Editable/non-editable sections
 * - Section identification for template binding
 * - Prefillable markers for auto-population
 */
export const DivExtension = Node.create<DivOptions>({
  name: 'div',
  group: 'block',
  content: 'block+',
  defining: true,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div',
        getAttrs: (node) => {
          if (typeof node === 'string') {
            return {};
          }
          const element = node as HTMLElement;
          return {
            class: element.getAttribute('class') || null,
            style: element.getAttribute('style') || null,
            editable: element.getAttribute('data-editable') !== 'false',
            sectionId: element.getAttribute('data-section-id') || null,
            sectionType: element.getAttribute('data-section-type') || null,
            prefillable: element.getAttribute('data-prefillable') === 'true',
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },

  addAttributes() {
    return {
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute('class'),
        renderHTML: (attributes) => {
          if (!attributes.class) {
            return {};
          }
          return {
            class: attributes.class,
          };
        },
      },
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => {
          if (!attributes.style) {
            return {};
          }
          return {
            style: attributes.style,
          };
        },
      },
      editable: {
        default: true,
        parseHTML: (element) =>
          element.getAttribute('data-editable') !== 'false',
        renderHTML: (attributes) => {
          if (attributes.editable === false) {
            return {
              'data-editable': 'false',
              contenteditable: 'false',
            };
          }
          return {};
        },
      },
      sectionId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-section-id'),
        renderHTML: (attributes) => {
          if (!attributes.sectionId) {
            return {};
          }
          return {
            'data-section-id': attributes.sectionId,
          };
        },
      },
      sectionType: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-section-type'),
        renderHTML: (attributes) => {
          if (!attributes.sectionType) {
            return {};
          }
          return {
            'data-section-type': attributes.sectionType,
          };
        },
      },
      prefillable: {
        default: false,
        parseHTML: (element) =>
          element.getAttribute('data-prefillable') === 'true',
        renderHTML: (attributes) => {
          if (attributes.prefillable === true) {
            return {
              'data-prefillable': 'true',
            };
          }
          return {};
        },
      },
    };
  },

  addCommands() {
    return {
      setDiv:
        (attributes) =>
        ({ commands }) => {
          return commands.wrapIn(this.name, attributes);
        },
      toggleDiv:
        (attributes) =>
        ({ commands }) => {
          return commands.toggleWrap(this.name, attributes);
        },
    };
  },
});
